import { RenderObject } from '../object/RenderObject';
import { AnimationFrame } from './AnimationFrame';

export class AnimationState {
  public renderObject: RenderObject;
  public currentState: string = 'Idle';
  private animationStart: number;
  private pausedAt?: number;

  private previousState?: string;
  private blendStart?: number;
  private blendDuration: number = 300; // ms

  public allowedStates: StateDefinition = {};
  private warnedStates = new Set<string>();
  private warnedClips = new Set<string>();

  constructor(object: RenderObject) {
    this.renderObject = object;
    this.animationStart = Date.now();
  }

  protected resolveClipId(stateName: string): string {
    return stateName;
  }

  public pause() {
    if (this.pausedAt === undefined) {
      this.pausedAt = Date.now();
    }
  }

  public resume() {
    if (this.pausedAt !== undefined) {
      // Shift animationStart forward by the paused duration so the animation
      // continues from the frame it was at when paused.
      const pausedDuration = Date.now() - this.pausedAt;
      this.animationStart += pausedDuration;
      if (this.blendStart !== undefined) {
        this.blendStart += pausedDuration;
      }
      this.pausedAt = undefined;
    }
  }

  public get paused(): boolean {
    return this.pausedAt !== undefined;
  }

  public switchState(newState: string) {
    let state = this.allowedStates[newState];
    if (state && newState !== this.currentState) {
      // Only blend if the previous state is valid (has an entry in allowedStates)
      if (this.allowedStates[this.currentState]) {
        this.previousState = this.currentState;
        this.blendStart = Date.now();
      }
      this.currentState = newState;
      this.animationStart = Date.now();
    }
  }

  public getFrames(): FrameSet {
    const state = this.allowedStates[this.currentState];
    if (!state) {
      if (!this.warnedStates.has(this.currentState)) {
        this.warnedStates.add(this.currentState);
        const available = Object.keys(this.allowedStates);
        const isIdentityMapping = this.constructor === AnimationState;
        const hint = isIdentityMapping
          ? ` (no _ANIMATION_STATE configured — using raw clip names as states)`
          : '';
        console.warn(
          `[AnimationState] State "${this.currentState}" not found on "${this.renderObject.mesh.reference}"${hint}. ` +
            `Available: [${available.join(', ')}]`,
        );
      }
      return {
        frames: {},
        alpha: 0,
        state: this.currentState,
        stateName: this.currentState,
        frameIndex: 0,
      };
    }
    const now = this.pausedAt ?? Date.now();
    const joints = this.renderObject.animation!.joints;
    const clipId = this.resolveClipId(this.currentState);

    // Compute frame index and interpolation alpha for current clip
    const { frameIndex, alpha, last } = this.computeFrameIndex(
      clipId,
      state.speed,
      this.animationStart,
      now,
    );

    // Build frame map using computed index (all joints share same timebase)
    const frames: { [key: string]: AnimationFrame } = {};
    // For attachment interpolation: prevFrames is the frame before frameIndex,
    // alpha is how far between prevFrames and frames we are.
    let prevFrames: { [key: string]: AnimationFrame } | undefined;
    for (const joint of joints) {
      const jointFrames = joint.frames[clipId];
      frames[joint.name] = jointFrames[Math.min(frameIndex, jointFrames.length - 1)];
      if (alpha > 0 && frameIndex > 0) {
        if (!prevFrames) prevFrames = {};
        prevFrames[joint.name] = jointFrames[frameIndex - 1];
      }
    }

    const result: FrameSet = {
      frames,
      prevFrames,
      alpha,
      frameIndex,
      state: clipId,
      stateName: this.currentState,
    };

    // Compute blend data if transitioning
    if (this.previousState && this.blendStart) {
      const elapsed = now - this.blendStart;
      const weight = Math.min(elapsed / this.blendDuration, 1.0);

      if (weight < 1.0) {
        const prevStateConfig = this.allowedStates[this.previousState];
        const prevClipId = this.resolveClipId(this.previousState);
        const { frameIndex: prevFrameIndex } = this.computeFrameIndex(
          prevClipId,
          prevStateConfig.speed,
          this.blendStart,
          now,
        );

        const blendFrames: { [key: string]: AnimationFrame } = {};
        for (const joint of joints) {
          const jointFrames = joint.frames[prevClipId];
          blendFrames[joint.name] = jointFrames[Math.min(prevFrameIndex, jointFrames.length - 1)];
        }

        result.blend = {
          frames: blendFrames,
          state: prevClipId,
          stateName: this.previousState,
          frameIndex: prevFrameIndex,
          weight,
        };
      } else {
        this.previousState = undefined;
        this.blendStart = undefined;
      }
    }

    // Handle clip completion
    if (last) {
      switch (state.repeat) {
        case 'always':
          this.animationStart = Date.now();
          break;
        case 'once':
          if (state.nextState) {
            this.switchState(state.nextState);
          }
          break;
      }
    }

    return result;
  }

  private computeFrameIndex(
    clipId: string,
    speed: number,
    startTime: number,
    now: number,
  ): { frameIndex: number; alpha: number; last: boolean } {
    const joints = this.renderObject.animation!.joints;
    const referenceFrames = joints[0].frames[clipId];
    if (!referenceFrames) {
      if (!this.warnedClips.has(clipId)) {
        this.warnedClips.add(clipId);
        console.warn(
          `[AnimationState] Animation clip "${clipId}" not found on "${this.renderObject.mesh.reference}". ` +
            `Available clips in GLB: [${Object.keys(joints[0].frames).join(', ')}]`,
        );
      }
      return { frameIndex: 0, alpha: 0, last: true };
    }

    let frameIndex = referenceFrames.length - 1;
    let alpha = 0;
    let last = true;

    for (let f = 0; f < referenceFrames.length; f++) {
      if (startTime + referenceFrames[f].timeStep * speed * 1000 > now) {
        frameIndex = f;
        last = false;
        // Compute interpolation alpha between previous frame and this one
        if (f > 0) {
          const prevTime = startTime + referenceFrames[f - 1].timeStep * speed * 1000;
          const currTime = startTime + referenceFrames[f].timeStep * speed * 1000;
          alpha = (now - prevTime) / (currTime - prevTime);
        }
        break;
      }
    }

    return { frameIndex, alpha, last };
  }
}

export type StateDefinition = {
  [key: string]: {
    repeat: string;
    speed: number;
    nextState?: string;
  };
};

export type FrameSet = {
  frames: { [key: string]: AnimationFrame };
  prevFrames?: { [key: string]: AnimationFrame };
  alpha: number;
  state: string;
  stateName: string;
  frameIndex: number;
  blend?: {
    frames: { [key: string]: AnimationFrame };
    state: string;
    stateName: string;
    frameIndex: number;
    weight: number;
  };
};
